import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import { Reveal } from "../components/Reveal";

const SIZES = ["S", "M", "L", "XL", "XXL", "28", "30", "32", "34", "36", "One Size"];
const COLOURS = ["Off White", "Washed Black", "Forest Green", "Espresso Brown", "Midnight Navy", "Stone Grey", "Burgundy"];
const COLOUR_HEX = { "Off White": "#FDFBF7", "Washed Black": "#1C1B1A", "Forest Green": "#183A2E", "Espresso Brown": "#4A3428", "Midnight Navy": "#1E2A3A", "Stone Grey": "#A7A39B", "Burgundy": "#6A1E2C" };
const PRICE_BANDS = [[null, 1500, "Under ₹1,500"], [1500, 2500, "₹1,500 – ₹2,500"], [2500, null, "Above ₹2,500"]];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const category = params.get("category") || "";
  const q = params.get("q") || "";
  const sort = params.get("sort") || "";
  const color = params.get("color") || "";
  const size = params.get("size") || "";
  const gender = params.get("gender") || "";
  const tag = params.get("tag") || "";

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const query = new URLSearchParams();
    for (const k of ["category", "q", "sort", "color", "size", "gender", "tag"]) {
      const v = params.get(k);
      if (v) query.set(k, v);
    }
    api.get(`/products?${query.toString()}`)
      .then((r) => setProducts(r.data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [params]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next);
  };

  const heading = useMemo(() => {
    if (q) return `“${q}”`;
    const g = gender === "men" ? "Men's" : gender === "women" ? "Women's" : "";
    const cat = categories.find((c) => c.slug === category);
    if (tag === "bestseller") return g ? `${g} Best Sellers` : "Best Sellers";
    if (g && cat) return `${g} ${cat.name}`;
    if (g) return gender === "men" ? "Men" : "Women";
    if (cat) return cat.name;
    if (sort === "newest") return "New Arrivals";
    return "Shop All";
  }, [q, category, categories, gender, tag, sort]);

  useEffect(() => {
    document.title = `${heading} — Velour & Vane`;
  }, [heading]);

  const filterPanel = (
    <div className="space-y-10">
      <div>
        <p className="vv-overline mb-5">Shop For</p>
        <div className="flex gap-2">
          {[["", "All"], ["men", "Men"], ["women", "Women"]].map(([val, label]) => (
            <button key={label} data-testid={`filter-gender-${label.toLowerCase()}`} onClick={() => setParam("gender", val)}
              className={`text-[10px] uppercase tracking-[0.2em] border px-3.5 py-2 transition-colors ${gender === val ? "bg-vv-black text-vv-ivory border-vv-black" : "border-vv-black/20 hover:border-vv-black"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="vv-overline mb-5">Category</p>
        <ul className="space-y-3">
          <li>
            <button data-testid="filter-category-all" onClick={() => setParam("category", "")}
              className={`text-sm ${!category ? "font-semibold" : "text-vv-muted hover:text-vv-black"}`}>All Products</button>
          </li>
          {categories.map((c) => (
            <li key={c.slug}>
              <button data-testid={`filter-category-${c.slug}`} onClick={() => setParam("category", c.slug)}
                className={`text-sm ${category === c.slug ? "font-semibold" : "text-vv-muted hover:text-vv-black"}`}>
                {c.name} <span className="text-[10px] text-vv-stone">({c.count})</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <p className="vv-overline mb-5">Colour</p>
        <div className="flex flex-wrap gap-3">
          {COLOURS.map((c) => (
            <button key={c} data-testid={`filter-colour-${c.toLowerCase().replace(/\s/g, "-")}`} title={c}
              onClick={() => setParam("color", color === c ? "" : c)}
              className={`w-7 h-7 rounded-full border ${color === c ? "ring-2 ring-offset-2 ring-vv-black ring-offset-vv-ivory" : "border-vv-black/20"}`}
              style={{ background: COLOUR_HEX[c] }} />
          ))}
        </div>
      </div>
      <div>
        <p className="vv-overline mb-5">Size</p>
        <div className="flex flex-wrap gap-2">
          {SIZES.map((s) => (
            <button key={s} data-testid={`filter-size-${s.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => setParam("size", size === s ? "" : s)}
              className={`text-[10px] tracking-wider border px-2.5 py-1.5 transition-colors ${size === s ? "bg-vv-black text-vv-ivory border-vv-black" : "border-vv-black/20 hover:border-vv-black"}`}>
              {s}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="vv-overline mb-5">Price</p>
        <ul className="space-y-3">
          {PRICE_BANDS.map(([lo, hi, label], i) => {
            const active = (params.get("min_price") || "") === String(lo ?? "") && (params.get("max_price") || "") === String(hi ?? "");
            return (
              <li key={i}>
                <button data-testid={`filter-price-${i}`}
                  onClick={() => {
                    const next = new URLSearchParams(params);
                    if (active) { next.delete("min_price"); next.delete("max_price"); }
                    else {
                      if (lo != null) next.set("min_price", lo); else next.delete("min_price");
                      if (hi != null) next.set("max_price", hi); else next.delete("max_price");
                    }
                    setParams(next);
                  }}
                  className={`text-sm ${active ? "font-semibold" : "text-vv-muted hover:text-vv-black"}`}>{label}</button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );

  return (
    <main data-testid="shop-page" className="px-4 sm:px-8 lg:px-16 max-w-[1600px] mx-auto py-12 md:py-20 min-h-screen">
      <Reveal>
        <p className="vv-overline text-vv-muted mb-4">
          <Link to="/" className="hover:text-vv-black">Home</Link> / {heading}
        </p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight">{heading.toUpperCase()}</h1>
      </Reveal>

      <div className="flex items-center justify-between mt-10 mb-8 border-y border-vv-black/10 py-4">        <button data-testid="filters-toggle-button" onClick={() => setFiltersOpen(true)} className="lg:hidden flex items-center gap-2 text-[11px] uppercase tracking-[0.2em]">
          <SlidersHorizontal size={14} strokeWidth={1.5} /> Filters
        </button>
        <p data-testid="results-count" className="hidden lg:block text-[11px] uppercase tracking-[0.2em] text-vv-muted">
          {products.length} {products.length === 1 ? "piece" : "pieces"}
        </p>
        <select data-testid="sort-select" value={sort} onChange={(e) => setParam("sort", e.target.value)}
          className="bg-transparent text-[11px] uppercase tracking-[0.2em] outline-none cursor-pointer">
          <option value="">Sort: Featured</option>
          <option value="newest">Newest</option>
          <option value="price-asc">Price: Low to High</option>
          <option value="price-desc">Price: High to Low</option>
          <option value="name">Name A–Z</option>
        </select>
      </div>

      <div className="grid lg:grid-cols-[240px_1fr] gap-12">
        <aside className="hidden lg:block">{filterPanel}</aside>
        <div>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-x-3 gap-y-8 md:gap-x-5">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[3/4] bg-vv-elevated" />
                  <div className="h-3 bg-vv-elevated mt-4 w-2/3" />
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div data-testid="no-results" className="py-24 text-center">
              <p className="font-display text-3xl">Nothing here — yet.</p>
              <p className="text-vv-muted text-sm mt-3">Try removing a filter or exploring the full collection.</p>
              <button data-testid="clear-filters-button" onClick={() => setParams({})} className="vv-btn-outline-dark mt-8">Clear Filters</button>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-x-3 gap-y-8 md:gap-x-5 md:gap-y-10">
              {products.map((p, i) => (
                <Reveal key={p.id} delay={(i % 3) * 0.05}>
                  <ProductCard product={p} index={i} />
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </div>

      {filtersOpen && (
        <div className="fixed inset-0 z-[80] lg:hidden">
          <div className="absolute inset-0 bg-vv-black/40" onClick={() => setFiltersOpen(false)} />
          <div data-testid="mobile-filter-drawer" className="absolute left-0 top-0 bottom-0 w-[85%] max-w-sm bg-vv-ivory p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-8">
              <p className="vv-overline">Filters</p>
              <button data-testid="filters-close-button" onClick={() => setFiltersOpen(false)} aria-label="Close filters"><X size={20} strokeWidth={1.25} /></button>
            </div>
            {filterPanel}
            <button data-testid="filters-apply-button" onClick={() => setFiltersOpen(false)} className="vv-btn-dark w-full mt-10">
              View {products.length} Pieces
            </button>
          </div>
        </div>
      )}
    </main>
  );
}
